import { CloseButton, Flex, Text } from '@chakra-ui/react'
import { useMemo } from 'react'
import { Tooltip } from '~/components/ui/tooltip'
import { SelectedFilterData } from '~/types/Shared'

import { textStyleKeys } from '@mangovoice/design-system'

interface FilterPillsProps {
  activeFilters: SelectedFilterData[]
  onRemoveFilter: (filter: SelectedFilterData) => void
  onClearAllFilters: () => void
}

const MAX_VISIBLE_FILTERS = 3

export const FilterPills = ({
  activeFilters,
  onRemoveFilter,
  onClearAllFilters,
}: FilterPillsProps) => {
  // Determine if we should show individual pills (1-3) or summary pill (4+)
  const shouldShowSummaryPill = activeFilters.length > MAX_VISIBLE_FILTERS
  const visibleFilters = shouldShowSummaryPill ? [] : activeFilters

  // Create tooltip content for summary pill - shows all active filters
  const summaryTooltipContent = useMemo(() => {
    if (!shouldShowSummaryPill) return ''
    return activeFilters.map((filter) => filter.itemTitle).join(', ')
  }, [activeFilters, shouldShowSummaryPill])

  if (shouldShowSummaryPill) {
    // Render summary pill when there are 4+ filters
    return (
      <Tooltip
        content={summaryTooltipContent}
        showArrow
        positioning={{ placement: 'top' }}
        contentProps={{
          bg: 'fg.subtle',
          color: 'brand.contrast',
          px: '0.75rem',
          py: '0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.875rem',
          maxW: '20rem',
        }}
      >
        <Flex
          css={{
            height: '1.75rem',
            px: '0.75rem',
            py: '0.375rem',
            bg: 'brand.subtle',
            border: '1px solid',
            borderColor: 'brand.solid',
            borderRadius: '1rem',
            cursor: 'default',
            gap: '0.25rem',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Text
            textStyle={textStyleKeys.Body2}
            css={{ color: 'brand.solid', fontWeight: 400 }}
          >
            {`${activeFilters.length} Filters selected`}
          </Text>
          <CloseButton
            onClick={onClearAllFilters}
            size="xs"
            css={{
              color: 'brand.solid',
              width: '1rem',
              height: '1rem',
              minW: '1rem',
              borderRadius: '0.125rem',
              _hover: {
                bg: 'brand.muted',
                color: 'brand.solid',
              },
            }}
          />
        </Flex>
      </Tooltip>
    )
  }

  // Render individual filter pills when there are 1-3 filters
  return (
    <>
      {visibleFilters.map((filter, index) => (
        <Flex
          key={`${filter.sectionTitle}-${filter.itemTitle}-${index}`}
          css={{
            height: '1.75rem',
            px: '0.75rem',
            py: '0.375rem',
            bg: 'brand.subtle',
            border: '1px solid',
            borderColor: 'brand.solid',
            borderRadius: '1rem',
            gap: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <Text
            textStyle={textStyleKeys.Body2}
            css={{
              color: 'brand.solid',
              fontWeight: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '20ch',
            }}
            title={filter.itemTitle}
          >
            {filter.itemTitle}
          </Text>
          <CloseButton
            onClick={() => onRemoveFilter(filter)}
            size="xs"
            css={{
              color: 'brand.solid',
              width: '1rem',
              height: '1rem',
              minW: '1rem',
              borderRadius: '0.125rem',
              _hover: {
                bg: 'brand.muted',
                color: 'brand.solid',
              },
            }}
          />
        </Flex>
      ))}
    </>
  )
}
